import BoldSmallText from '@/components/BoldSmallText';

import {
  deliverySpecificWorkingDaySelector,
  deliveryStateSelector,
  deliveryWeeklyWorkingDaySelector,
  handleSaveDeliverySpecificWorkingDays,
  handleSaveDeliveryWeeklyWorkingDays,
} from '@/redux/slice/delivery.slice';
import { errorFieldsSelector } from '@/redux/slice/error.slice';
import { getWeekdaysFromSpecificWorkingDays, groupDatesByYearMonthDay, specificDateProcess } from '@/utils';
import { BlockStack, Button, InlineError, InlineGrid } from '@shopify/polaris';
import { PlusIcon } from '@shopify/polaris-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import SpecificDatePicker from './DeliveryDatePicker/SpecificDatePicker';
import SpecificDateTable from './DeliveryDatePicker/SpecificDateTable';
import { IHoliday, ISpecificWorkingDay } from '@/types/components/day';

const SpecificDate = () => {
  const { t } = useTranslation('deliverySettings');
  const deliverySpecificDays = useSelector(deliverySpecificWorkingDaySelector);
  const deliveryState = useSelector(deliveryStateSelector);
  const deliveryWeeklyWorkingDays = useSelector(deliveryWeeklyWorkingDaySelector);
  const errorFields = useSelector(errorFieldsSelector);
  const dispatch = useDispatch();
  const { id } = useParams();

  const [currentDateRange, setCurrentDateRange] = useState<{ start: Date; end: Date }>();

  const handleOnAddDates = () => {
    if (!currentDateRange) {
      return;
    }
    const groupedDates = groupDatesByYearMonthDay(currentDateRange);

    const mergedSpecificDays = specificDateProcess(groupedDates, deliverySpecificDays);

    const weekdaysFromSpecificDates = getWeekdaysFromSpecificWorkingDays(
      mergedSpecificDays as unknown as ISpecificWorkingDay[],
    );
    dispatch(
      handleSaveDeliveryWeeklyWorkingDays(
        deliveryWeeklyWorkingDays.map((item) => ({
          ...item,
          isEnable: weekdaysFromSpecificDates.includes(item.day),
        })),
      ),
    );

    const mergedSpecificWorkingDays = mergedSpecificDays.map((item, index) => ({
      id: index + 1,
      settingId: Number(id),
      year: item.year,
      month: item.month,
      dates: item.dates,
      updatedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
    }));

    dispatch(handleSaveDeliverySpecificWorkingDays(mergedSpecificWorkingDays));
    setCurrentDateRange(undefined);
  };

  const handleSetSpecificDates = (data: ISpecificWorkingDay[] | IHoliday[]) =>
    dispatch(handleSaveDeliverySpecificWorkingDays(data));

  const handleSetDeliveryWeeklyWorkingDays = (data: number[]) => {
    dispatch(
      handleSaveDeliveryWeeklyWorkingDays(
        deliveryWeeklyWorkingDays.map((day) => ({
          ...day,
          isEnable: data.includes(day.day),
        })),
      ),
    );
  };

  return (
    <BlockStack gap="400">
      <BoldSmallText>{t('specificDate.heading')}</BoldSmallText>
      <InlineGrid columns="1fr auto" gap="200">
        <SpecificDatePicker dateRange={currentDateRange} setDateRange={setCurrentDateRange} />
        <Button icon={PlusIcon} onClick={handleOnAddDates} disabled={!currentDateRange} fullWidth>
          {t('specificDate.actions.add')}
        </Button>
      </InlineGrid>
      {deliveryState.isSaving && errorFields.errorFields?.errorSpecificDays && (
        <InlineError fieldID="" message={t('error.errorSpecificDays')} />
      )}
      <SpecificDateTable
        handleSetSpecificDates={handleSetSpecificDates}
        handleSetDeliveryWeeklyWorkingDays={handleSetDeliveryWeeklyWorkingDays}
        specificDates={deliverySpecificDays}
      />
    </BlockStack>
  );
};

export default SpecificDate;
